import { Light, LightFields, LightProperties } from '../../core/Light';
import { Vector3, Euler } from 'three';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import * as THREE from 'three';
import { colors } from '@/constants/theme';
import { Field } from '@/types/core/Field';

export interface AmbientLightProperties extends LightProperties {
  intensity: number;
  color: string;
}

// Create a reusable Ambient Light component that can be used in both preview and world
const AmbientLightComponent = forwardRef<THREE.AmbientLight, { properties: AmbientLightProperties; isPreview?: boolean }>((props, ref) => {
  const { properties, isPreview } = props;
  const { intensity, color } = properties;
  const localRef = useRef<THREE.AmbientLight>(null);

  // Forward the ref to parent
  useImperativeHandle(ref, () => localRef.current!, []);

  return (
    <>
      <ambientLight
        ref={localRef}
        intensity={intensity}
        color={color}
      />
      {isPreview && (
        <>
          {/* Ground plane */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -2, 0]}>
            <planeGeometry args={[10, 10]} />
            <meshStandardMaterial color={colors.contrast} />
          </mesh>
          
          {/* Demo objects to show ambient lighting */}
          <mesh position={[-1.5, 0, 0]}>
            <boxGeometry args={[1, 1, 1]} />
            <meshStandardMaterial color={colors.primary} />
          </mesh>
          
          <mesh position={[1.5, 0, 0]}>
            <sphereGeometry args={[0.6, 32, 32]} />
            <meshStandardMaterial color={colors.muted} />
          </mesh>
          
          {/* Visual indicator for ambient light */}
          <mesh position={[0, 2, 0]} scale={0.3}>
            <sphereGeometry args={[1, 16, 16]} />
            <meshBasicMaterial color={color} transparent opacity={0.5} />
          </mesh>
        </>
      )}
    </>
  );
});

export const AmbientLightItem: Light<AmbientLightProperties> = {
  id: '',
  name: 'Ambient Light',
  type: 'light',
  subtype: 'ambient',
  icon: 'Sun',
  properties: {
    intensity: 0.5,
    color: '#ffffff',
    transform: {
      position: new Vector3(0, 0, 0),
      rotation: new Euler(),
      scale: new Vector3(1, 1, 1),
    },
  },
  fields: [
    ...LightFields,
    // Ambient light doesn't need transform fields as it affects the entire scene
  ],
  render: (properties: AmbientLightProperties, isPreview: boolean, ref: React.RefObject<THREE.Object3D | null>) => {
    return <AmbientLightComponent properties={properties} isPreview={isPreview} ref={ref as React.RefObject<THREE.AmbientLight>} />;
  }
}; 